import React, { useState, useEffect, useMemo } from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Fade from "@mui/material/Fade";

import { useDispatch, useSelector } from "react-redux";
import { getQualifications } from "../../Slices/qualifications";
import { getBadges } from "../../Slices/badges";

import SharePopover from "../../Components/DropdownShareButton";
import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
import { Footer } from "../../Components/Footer";
import HeroTitleBox from "../../Components/HeroTitleBox";
import { Pagination } from "@mui/material";


const qualificationMatrix = [
  {
    topic: "Gastronomia",
    categories: [
      "Asystowanie w przygotowaniu potraw",
      "Przygotowywanie i podawanie kawy oraz napojów kawowych",
      "Przygotowanie deserów restauracyjnych",
      "Przygotowywanie potraw zgodnie z trendami rynkowymi i zasadami zdrowego żywienia",
      "Przygotowanie kuchni do procesu produkcyjnego i asystowanie w procesie produkcyjnym",
      "Serwis napojów mieszanych i alkoholi",
    ],
  },

  {
    topic: "Informatyka i technologie cyfrowe",
    categories: [
      "Certyfikat umiejętności komputerowych – poziom podstawowy",
      "Modelowanie i skanowanie 3D",
      "Pozyskiwanie i edycja danych przestrzennych",
      "Projektowanie automatyzacji procesów biznesowych",
      "Programowanie i obsługiwanie procesu druku 3D",
      "Tworzenie witryn internetowych",
      "Wykorzystywanie technologii informacyjno-komunikacyjnych (TIK) w kształceniu",
    ],
  },

  {
    topic: "Grafika i multimedia",
    categories: [
      "Projektowanie grafiki komputerowej",
      "Modelowanie i skanowanie 3D",
      "Programowanie i obsługiwanie procesu druku 3D",
    ],
  },

  {
    topic: "Biznes i przedsiębiorczość",
    categories: [
      "Doradzanie w zakresie zakładania i prowadzenia działalności gospodarczej",
      "Wdrażanie i koordynowanie procesów biznesowych (Lean Office)",
      "Zarządzanie zrównoważonym rozwojem organizacji",
    ],
  },

  {
    topic: "Administracja i biuro",
    categories: [
      "Prowadzenie obsługi biura",
      "Zarządzanie procesem pozyskiwania i obsługi inwestora w administracji publicznej",
      "Wdrażanie i koordynowanie procesów biznesowych (Lean Office)",
    ],
  },

  {
    topic: "Marketing i komunikacja",
    categories: [
      "Budowanie wizerunku osobistego",
      "Budowanie wizerunku pracodawcy (employer branding)",
      "Planowanie i prowadzenie działań komunikacyjnych w sytuacjach kryzysu wizerunkowego",
      "Planowanie, tworzenie i dystrybuowanie treści marketingowych (content marketing)",
      "Posługiwanie się technikami emisji głosu",
      "Występowanie publiczne",
    ],
  },

  {
    topic: "Sprzedaż i obsługa klienta",
    categories: [
      "Prowadzenie negocjacji",
      "Projektowanie i prowadzenie treningu rozwoju kompetencji sprzedażowych",
      "Zarządzanie relacjami z klientem z wykorzystaniem systemu CRM",
    ],
  },

  {
    topic: "Prawo i mediacje",
    categories: [
      "Prowadzenie mediacji sądowych i pozasądowych w sprawach cywilnych",
      "Prowadzenie mediacji sądowych i pozasądowych w sprawach gospodarczych",
    ],
  },

  {
    topic: "Zarządzanie i kompetencje miękkie",
    categories: [
      "Zarządzanie konfliktami w organizacji",
      "Występowanie publiczne",
      "Prowadzenie negocjacji",
      "Budowanie wizerunku osobistego",
    ],
  },

  {
    topic: "Edukacja",
    categories: [
      "Wspieranie zdolnych i uzdolnionych dzieci oraz młodzieży",
      "Wykorzystywanie technologii informacyjno-komunikacyjnych (TIK) w kształceniu",
    ],
  },

  {
    topic: "Budownictwo",
    categories: [
      "Montowanie stolarki budowlanej",
    ],
  },
];

const normalizeText = (value) => {
  if (!value) {
    return "";
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
};

export default function Qualifications() {
  useDocumentTitle("kwalifikacje");

  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { qualifications, totalPages } = useSelector(
    (state) => state.qualifications
  );

  const badges = useSelector(
    (state) => state.badges
  );

  const filteredBadges = useMemo(
    () =>
      badges
        .filter((badge) => badge.attributes?.badgeType === "Odznaka złota")
        .map((badge) => badge.attributes?.badgeName)
        .filter(Boolean),
    [badges]
  );

  const [displaySelected, setDisplaySelected] = useState('all');

  useEffect(() => {
    dispatch(getQualifications({ page: page, perPage: perPage }));

    if (user?.user?.email) {
      dispatch(getBadges({ email: user.email, user: user.jwt }));
    }
  }, [dispatch, user?.user?.email, user?.jwt, page, perPage]);



  const isOwnedQualification = (qualification) => {
    const qualificationTitle = normalizeText(
      qualification.title?.rendered
    );

    const qualificationSlug = normalizeText(
      qualification.slug
    );

    return filteredBadges.some((type) => {
      const normalizedType = normalizeText(type);

      return (
        normalizedType === qualificationTitle ||
        normalizedType === qualificationSlug
      );
    });
  };

  const ownedQualifications = useMemo(() => {
    if (!qualifications?.length || !filteredBadges.length) {
      return [];
    }

    return qualifications.filter(isOwnedQualification);
  }, [qualifications, filteredBadges]);

  // const userTopics = useMemo(() => {
  //   if (!ownedQualifications.length) {
  //     return [];
  //   }

  //   const ownedTitles = ownedQualifications.map((qualification) =>
  //     normalizeText(qualification.title?.rendered)
  //   );

  //   return qualificationMatrix.filter((topic) =>
  //     topic.categories.some((category) =>
  //       ownedTitles.includes(normalizeText(category))
  //     )
  //   );
  // }, [ownedQualifications]);

  const userTopics = useMemo(() => {
    if (!filteredBadges?.length) {
      return [];
    }

    const ownedTitles = filteredBadges.map((badge) =>
      normalizeText(badge)
    );

    return qualificationMatrix.filter((topic) =>
      topic.categories.some((category) =>
        ownedTitles.includes(normalizeText(category))
      )
    );
  }, [filteredBadges]);

  const recommendedQualifications = useMemo(() => {
    if (!qualifications?.length || !userTopics.length) {
      return [];
    }

    const recommendations = [];

    userTopics.forEach((topic) => {
      topic.categories.forEach((category) => {
        const categoryNormalized = normalizeText(category);

        const matches = qualifications.filter(
          (qualification) =>
            normalizeText(
              qualification.title?.rendered
            ) === categoryNormalized
        );

        matches.forEach((qualification) => {

          if (isOwnedQualification(qualification)) {
            return;
          }

          const alreadyAdded = recommendations.some(
            (item) => item.id === qualification.id
          );

          if (alreadyAdded) {
            return;
          }

          recommendations.push({
            ...qualification,
            recommendedTopic: topic.topic,
          });
        });
      });
    });

    return recommendations;
  }, [
    qualifications,
    userTopics,
    filteredBadges,
  ]);

  const displayedQualifications = displaySelected === 'selected'
    ? recommendedQualifications
    : qualifications;


  return (
    <>
      <CssBaseline />

      <main>
        <HeroTitleBox
          title="Kwalifikacje dla Ciebie"
          subtitle="Na bazie posiadanych przez Ciebie certyfikatów
              wybraliśmy kwalifikacje, którymi możesz być
              zainteresowany."
          ownJsx={
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent="center"
              sx={{ pt: 4 }}
            >
              <Button
                variant={
                  displaySelected === 'selected'
                    ? "contained"
                    : "outlined"
                }
                onClick={() => setDisplaySelected('selected')}
                aria-pressed={displaySelected === 'selected'}
                sx={{ color: displaySelected === 'selected' ? "#fff" : theme => theme.palette.texts.primary, border: `1px solid ${displaySelected === 'selected' ? theme => theme.palette.texts.primary : "#ccc"}` }}
              >
                Wybrane dla Ciebie
              </Button>

              <Button
                variant={
                  displaySelected === 'all'
                    ? "contained"
                    : "outlined"
                }
                onClick={() => setDisplaySelected('all')}
                aria-pressed={displaySelected === 'all'}
                sx={{ color: displaySelected === 'all' ? "#fff" : theme => theme.palette.texts.primary, border: `1px solid ${displaySelected === 'all' ? theme => theme.palette.texts.primary : "#ccc"}` }}
              >
                Wszystkie
              </Button>
            </Stack>
          }
          customWidth={300}
        />

        <Container
          component="section"
          maxWidth="md"
          sx={{
            py: {
              xs: 4,
              md: 6,
            },
          }}
        >
          {!displayedQualifications?.length ? (
            <Typography
              align="center"
              color="text.secondary"
              sx={{ py: 6 }}
            >
              Nie znaleziono kwalifikacji.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {displayedQualifications.map(
                (qualification) => {
                  const {
                    id,
                    link,
                    title,
                    acf,
                    yoast_head_json,
                  } = qualification;

                  return (
                    <Fade
                      key={id}
                      in
                      timeout={400}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <Card
                          component="article"
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 3,
                            overflow: "hidden",
                            boxShadow: 1,
                            transition:
                              "box-shadow 0.2s, transform 0.2s",

                            "&:hover": {
                              transform:
                                "translateY(-3px)",
                              boxShadow: 4,
                            },

                            "&:focus-within": {
                              outline: "3px solid",
                              outlineColor:
                                "primary.main",
                              outlineOffset: "-3px",
                            },
                          }}
                        >
                          {yoast_head_json?.og_image
                            ?.length > 0 && (
                              <CardMedia
                                component="img"
                                height="180"
                                image={
                                  yoast_head_json
                                    .og_image[0].url
                                }
                                alt={
                                  title?.rendered ||
                                  "Obrazek kwalifikacji"
                                }
                                sx={{
                                  objectFit: "cover",
                                }}
                              />
                            )}

                          <CardContent
                            sx={{
                              flexGrow: 1,
                              p: 3,
                            }}
                          >
                            <Typography
                              component="h2"
                              variant="h5"
                              color="text.primary"
                              sx={{
                                fontWeight: 600,
                                lineHeight: 1.3,
                                mb: 2,
                              }}
                            >
                              {title?.rendered}
                            </Typography>

                            <Stack spacing={1}>
                              {acf?.liczba_godzin && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  <strong>
                                    Liczba godzin:
                                  </strong>{" "}
                                  {acf.liczba_godzin}
                                </Typography>
                              )}

                              {acf?.czas_trwania_egzaminu && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    whiteSpace:
                                      "pre-line",
                                  }}
                                >
                                  <strong>
                                    Czas egzaminu:
                                  </strong>{" "}
                                  {
                                    acf.czas_trwania_egzaminu
                                  }
                                </Typography>
                              )}

                              {acf?.jezyk_szkolenia
                                ?.length > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <strong>
                                      Język szkolenia:
                                    </strong>{" "}
                                    {acf.jezyk_szkolenia.join(
                                      ", "
                                    )}
                                  </Typography>
                                )}
                            </Stack>
                          </CardContent>

                          <CardActions
                            sx={{
                              px: 3,
                              pb: 3,
                              pt: 0,
                              justifyContent:
                                "space-between",
                            }}
                          >
                            {/* <SharePopover
                              shareUrl={link}
                            /> */}

                            <SharePopover
                              shareUrl={link}
                              shareTitle={`Kwalifikacja ZSK - ${title?.rendered}`}
                              shareText={`Zapoznaj sie z kwalifikacją z tematu: ${title?.rendered}!`}
                            />

                            <Button
                              component="a"
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              size="small"
                            >
                              Dowiedz się więcej
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    </Fade>
                  );
                }
              )}
            </Grid>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Pagination count={totalPages} size="large" onChange={(event, value) => setPage(value)} sx={{ mt: 4, textAlign: 'center' }} />
          </Box>
        </Container>
      </main>

      <Footer />
    </>
  );
}





// import React, { useState, useEffect, useMemo } from "react";
// import Button from "@mui/material/Button";
// import Grid from "@mui/material/Grid";
// import Stack from "@mui/material/Stack";
// import Box from "@mui/material/Box";
// import Typography from "@mui/material/Typography";
// import Container from "@mui/material/Container";
// import CssBaseline from "@mui/material/CssBaseline";
// import { useDispatch, useSelector } from "react-redux";
// import { getQualifications } from "../../Slices/qualifications";

// import Card from "@mui/material/Card";
// import CardActions from "@mui/material/CardActions";
// import CardContent from "@mui/material/CardContent";
// import CardMedia from "@mui/material/CardMedia";
// import DropdownShareButton from "../../Components/DropdownShareButton";
// import { Fade } from "@mui/material";
// import { getCertificates } from "../../Slices/certificates";
// import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
// import { Footer } from "../../Components/Footer";

// const qualificationMatrix = [
//   {
//     topic: "Gastronomia",
//     categories: [
//       "Asystowanie w przygotowaniu potraw",
//       "Przygotowywanie i podawanie kawy oraz napojów kawowych",
//       "Przygotowanie deserów restauracyjnych",
//       "Przygotowywanie potraw zgodnie z trendami rynkowymi i zasadami zdrowego żywienia",
//       "Przygotowanie kuchni do procesu produkcyjnego i asystowanie w procesie produkcyjnym",
//       "Serwis napojów mieszanych i alkoholi",
//     ],
//   },

//   {
//     topic: "Informatyka i technologie cyfrowe",
//     categories: [
//       "Certyfikat umiejętności komputerowych – poziom podstawowy",
//       "Modelowanie i skanowanie 3D",
//       "Pozyskiwanie i edycja danych przestrzennych",
//       "Projektowanie automatyzacji procesów biznesowych",
//       "Programowanie i obsługiwanie procesu druku 3D",
//       "Tworzenie witryn internetowych",
//       "Wykorzystywanie technologii informacyjno-komunikacyjnych (TIK) w kształceniu",
//     ],
//   },

//   {
//     topic: "Grafika i multimedia",
//     categories: [
//       "Projektowanie grafiki komputerowej",
//       "Modelowanie i skanowanie 3D",
//       "Programowanie i obsługiwanie procesu druku 3D",
//     ],
//   },

//   {
//     topic: "Biznes i przedsiębiorczość",
//     categories: [
//       "Doradzanie w zakresie zakładania i prowadzenia działalności gospodarczej",
//       "Wdrażanie i koordynowanie procesów biznesowych (Lean Office)",
//       "Zarządzanie zrównoważonym rozwojem organizacji",
//     ],
//   },

//   {
//     topic: "Administracja i biuro",
//     categories: [
//       "Prowadzenie obsługi biura",
//       "Zarządzanie procesem pozyskiwania i obsługi inwestora w administracji publicznej",
//       "Wdrażanie i koordynowanie procesów biznesowych (Lean Office)",
//     ],
//   },

//   {
//     topic: "Marketing i komunikacja",
//     categories: [
//       "Budowanie wizerunku osobistego",
//       "Budowanie wizerunku pracodawcy (employer branding)",
//       "Planowanie i prowadzenie działań komunikacyjnych w sytuacjach kryzysu wizerunkowego",
//       "Planowanie, tworzenie i dystrybuowanie treści marketingowych (content marketing)",
//       "Posługiwanie się technikami emisji głosu",
//       "Występowanie publiczne",
//     ],
//   },

//   {
//     topic: "Sprzedaż i obsługa klienta",
//     categories: [
//       "Prowadzenie negocjacji",
//       "Projektowanie i prowadzenie treningu rozwoju kompetencji sprzedażowych",
//       "Zarządzanie relacjami z klientem z wykorzystaniem systemu CRM",
//     ],
//   },

//   {
//     topic: "Prawo i mediacje",
//     categories: [
//       "Prowadzenie mediacji sądowych i pozasądowych w sprawach cywilnych",
//       "Prowadzenie mediacji sądowych i pozasądowych w sprawach gospodarczych",
//     ],
//   },

//   {
//     topic: "Zarządzanie i kompetencje miękkie",
//     categories: [
//       "Zarządzanie konfliktami w organizacji",
//       "Występowanie publiczne",
//       "Prowadzenie negocjacji",
//       "Budowanie wizerunku osobistego",
//     ],
//   },

//   {
//     topic: "Edukacja",
//     categories: [
//       "Wspieranie zdolnych i uzdolnionych dzieci oraz młodzieży",
//       "Wykorzystywanie technologii informacyjno-komunikacyjnych (TIK) w kształceniu",
//     ],
//   },

//   {
//     topic: "Budownictwo",
//     categories: [
//       "Montowanie stolarki budowlanej",
//     ],
//   },
// ];

// export default function Qualifications() {
//   useDocumentTitle("kwalifikacje");
//   const { user } = useSelector((state) => state.auth);
//   const [displaySelected, setDisplaySelected] = useState(false);
//   const [filtered, setFiltered] = useState();
//   const dispatch = useDispatch();
//   const certificates = useSelector((state) => state.certificates);

//   const qualifications = useSelector((state) => state.qualifications);

//   console.log(qualifications)

//   // const yourTypes = useSelector((state) =>
//   //   state.certificates.reduce(
//   //     (unique, item) =>
//   //       unique.includes(item.attributes?.type)
//   //         ? unique
//   //         : [...unique, item.attributes?.type],
//   //     []
//   //   )
//   // );

//   const yourTypes = useMemo(
//     () =>
//       certificates.reduce(
//         (unique, item) =>
//           unique.includes(item.attributes?.type)
//             ? unique
//             : [...unique, item.attributes?.type],
//         []
//       ),
//     [certificates]
//   );

//   const filteredQualifications = (topic) => {
//     const selectedTopic = qualificationMatrix.find(
//       (item) => item.topic === topic
//     );

//     if (!selectedTopic) {
//       return [];
//     }

//     return qualifications.filter((qualification) =>
//       selectedTopic.categories.includes(
//         qualification.title?.rendered
//       )
//     );
//   };

//   const filteredQualificationCategory = (category) => {
//     return qualifications.filter(
//       (qualification) =>
//         qualification.title?.rendered === category
//     );
//   };

//   /**
//    * Wszystkie kwalifikacje należące do danego tematu,
//    * które użytkownik posiada.
//    */
//   const filteredUserQualifications = (topic) => {
//     const selectedTopic = qualificationMatrix.find(
//       (item) => item.topic === topic
//     );

//     if (!selectedTopic) {
//       return [];
//     }

//     return qualifications.filter(
//       (qualification) =>
//         selectedTopic.categories.includes(
//           qualification.attributes?.type
//         ) &&
//         yourTypes.includes(qualification.attributes?.type)
//     );
//   };

//   useEffect(() => {
//     dispatch(getQualifications());

//     if (user?.user?.email) {
//       dispatch(getCertificates(user.email));
//     }
//   }, [dispatch, user?.user?.email]);

//   useEffect(() => {
//     if (!qualifications?.length) {
//       setFiltered([]);
//       return;
//     }

//     const result = qualificationMatrix.flatMap((item) =>
//       item.categories
//         .filter((category) => yourTypes.includes(category))
//         .map((category) => ({
//           topic: item.topic,
//           category,
//           qualifications: filteredQualificationCategory(category),
//         }))
//     );

//     setFiltered(result);
//   }, [qualifications, yourTypes]);

//   console.log(
//     "Marketing:",
//     filteredQualifications("Marketing i komunikacja")
//   );

//   console.log(
//     "Budowanie wizerunku:",
//     filteredQualificationCategory(
//       "Budowanie wizerunku osobistego"
//     )
//   );


//   return (
//     <>
//       <CssBaseline />
//       <main>
//         <Box
//           sx={{
//             bgcolor: "background.paper",
//             pt: { xs: 5, sm: 7 },
//             pb: { xs: 4, sm: 6 },
//           }}
//         >
//           <Container maxWidth="md">
//             <Typography
//               component="h1"
//               variant="h2"
//               align="center"
//               color="text.primary"
//               sx={{
//                 fontWeight: 700,
//                 fontSize: { xs: "2rem", sm: "2.75rem" },
//               }}
//             >
//               Kwalifikacje dla Ciebie
//             </Typography>

//             <Typography
//               component="p"
//               variant="body1"
//               align="center"
//               color="text.secondary"
//               sx={{
//                 maxWidth: 700,
//                 mx: "auto",
//                 mt: 2,
//                 lineHeight: 1.7,
//               }}
//             >
//               Na bazie posiadanych przez Ciebie certyfikatów wybraliśmy
//               kwalifikacje, którymi możesz być zainteresowany.
//             </Typography>

//             <Stack
//               direction={{ xs: "column", sm: "row" }}
//               spacing={2}
//               justifyContent="center"
//               sx={{ pt: 4 }}
//             >
//               <Button
//                 variant={displaySelected ? "contained" : "outlined"}
//                 onClick={() => setDisplaySelected(true)}
//                 aria-pressed={displaySelected}
//               >
//                 Wybrane dla Ciebie
//               </Button>

//               <Button
//                 variant={!displaySelected ? "contained" : "outlined"}
//                 onClick={() => setDisplaySelected(false)}
//                 aria-pressed={!displaySelected}
//               >
//                 Wszystkie
//               </Button>
//             </Stack>
//           </Container>
//         </Box>

//         <Container
//           component="section"
//           maxWidth="md"
//           sx={{ py: { xs: 4, md: 6 } }}
//         >
//           <Grid container spacing={3}>
//             {(displaySelected ? filtered : qualifications)?.map((qualification) => {
//               const {
//                 id,
//                 slug,
//                 link,
//                 title,
//                 acf,
//                 yoast_head_json
//               } = qualification;

//               return (
//                 <Fade key={id} in timeout={400}>
//                   <Grid item xs={12} sm={6}>
//                     <Card
//                       component="article"
//                       sx={{
//                         height: "100%",
//                         display: "flex",
//                         flexDirection: "column",
//                         border: "1px solid",
//                         borderColor: "divider",
//                         borderRadius: 3,
//                         overflow: "hidden",
//                         boxShadow: 1,
//                         transition: "box-shadow 0.2s, transform 0.2s",

//                         "&:hover": {
//                           transform: "translateY(-3px)",
//                           boxShadow: 4,
//                         },

//                         "&:focus-within": {
//                           outline: "3px solid",
//                           outlineColor: "primary.main",
//                           outlineOffset: "-3px",
//                         },
//                       }}
//                     >
//                       {yoast_head_json?.og_image && (
//                         <CardMedia
//                           component="img"
//                           height="180"
//                           image={
//                             `${yoast_head_json.og_image[0].url}`
//                           }
//                           alt={acf?.certyfikat_obrazek?.title || "Obrazek kwalifikacji"}
//                           sx={{
//                             objectFit: "cover",
//                           }}
//                         />
//                       )}

//                       <CardContent sx={{ flexGrow: 1, p: 3 }}>
//                         <Typography
//                           component="h2"
//                           variant="h5"
//                           color="text.primary"
//                           sx={{
//                             fontWeight: 600,
//                             lineHeight: 1.3,
//                             mb: 2,
//                           }}
//                         >
//                           {title?.rendered}
//                         </Typography>

//                         <Stack spacing={1}>
//                           {acf?.liczba_godzin && (
//                             <Typography variant="body2" color="text.secondary">
//                               <strong>Liczba godzin:</strong>{" "}
//                               {acf.liczba_godzin}
//                             </Typography>
//                           )}

//                           {acf?.czas_trwania_egzaminu && (
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{ whiteSpace: "pre-line" }}
//                             >
//                               <strong>Czas egzaminu:</strong>{" "}
//                               {acf.czas_trwania_egzaminu}
//                             </Typography>
//                           )}

//                           {acf?.jezyk_szkolenia?.length > 0 && (
//                             <Typography variant="body2" color="text.secondary">
//                               <strong>Język szkolenia:</strong>{" "}
//                               {acf.jezyk_szkolenia.join(", ")}
//                             </Typography>
//                           )}
//                         </Stack>
//                       </CardContent>

//                       <CardActions
//                         sx={{
//                           px: 3,
//                           pb: 3,
//                           pt: 0,
//                           justifyContent: "space-between",
//                         }}
//                       >
//                         <DropdownShareButton shareUrl={link} />

//                         <Button
//                           component="a"
//                           href={link}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                           variant="contained"
//                           size="small"
//                         >
//                           Dowiedz się więcej
//                         </Button>
//                       </CardActions>
//                     </Card>
//                   </Grid>
//                 </Fade>
//               );
//             })}
//           </Grid>
//         </Container>
//       </main>
//       {/* <main>
//         <Box
//           sx={{
//             bgcolor: "background.paper",
//             pt: 8,
//             pb: 6,
//           }}
//         >
//           <Container maxWidth="sm">
//             <Typography
//               component="h1"
//               variant="h2"
//               align="center"
//               color="text.primary"
//             >
//               Kwalifikacje dla Ciebie
//             </Typography>
//             <Typography
//               variant="h5"
//               align="center"
//               color="text.secondary"
//               paragraph
//             >
//               Na bazie posiadanych przez Ciebie certyfikatów wybraliśmy
//               Kwalifikacje, którymi możesz być zainteresowany.
//             </Typography>
//             <Stack
//               sx={{ pt: 4 }}
//               direction="row"
//               spacing={2}
//               justifyContent="center"
//             >
//               <Button
//                 variant={displaySelected ? "contained" : "outlined"}
//                 onClick={() => setDisplaySelected(true)}
//               >
//                 Wybrane dla Ciebie
//               </Button>
//               <Button
//                 variant={displaySelected ? "outlined" : "contained"}
//                 onClick={() => setDisplaySelected(false)}
//               >
//                 Wszystkie
//               </Button>
//             </Stack>
//           </Container>
//         </Box>
//         <Container sx={{ py: 8 }} maxWidth="md">
//           <Grid container spacing={4}>
//             {displaySelected &&
//               filtered.map(({ id, attributes }) => (
//                 <Fade key={id} in={true} timeout={400}>
//                   <Grid item key={id} xs={12} sm={6} md={6}>
//                     <Card sx={{ height: "100%" }}>
//                       <CardMedia
//                         component="img"
//                         alt="zdjęcie"
//                         height="140"
//                         image={
//                           attributes.src.data
//                             // ? `${UPLOADS_URL}${attributes.src.data[0].attributes.url}`
//                             ? `${attributes.src.data[0].attributes.url}`
//                             : null
//                         }
//                       />
//                       <CardContent>
//                         <Typography variant="h5" component="div">
//                           {attributes.name}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {attributes.desc}
//                         </Typography>
//                       </CardContent>
//                       <CardActions>
//                         <DropdownShareButton shareUrl={attributes.share_url} />
//                         <a
//                           href={attributes.share_url}
//                           style={{
//                             margin: "0px 0px 0px auto",
//                             textDecoration: "none",
//                           }}
//                           target="_blank"
//                         >
//                           <Button size="small">Dowiedz się więcej</Button>
//                         </a>
//                       </CardActions>
//                     </Card>
//                   </Grid>
//                 </Fade>
//               ))}
//             {!displaySelected &&
//               Array.isArray(qualifications) &&
//               qualifications.map(({ id, attributes }) => (
//                 <Fade key={id} in={true} timeout={400}>
//                   <Grid item key={id} xs={12} sm={6} md={6}>
//                     <Card sx={{ height: "100%" }}>
//                       <CardMedia
//                         component="img"
//                         alt="zdjęcie"
//                         height="140"
//                         image={
//                           attributes.src.data
//                             // ? `${UPLOADS_URL}${attributes.src.data[0].attributes.url}`
//                             ? `${attributes.src.data[0].attributes.url}`
//                             : null
//                         }
//                       />
//                       <CardContent>
//                         <Typography variant="h5" component="div">
//                           {attributes.name}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {attributes.desc}
//                         </Typography>
//                       </CardContent>
//                       <CardActions>
//                         <DropdownShareButton shareUrl={attributes.share_url} />
//                         <a
//                           href={attributes.share_url}
//                           style={{
//                             margin: "0px 0px 0px auto",
//                             textDecoration: "none",
//                           }}
//                           target="_blank"
//                         >
//                           <Button size="small">Dowiedz się więcej</Button>
//                         </a>
//                       </CardActions>
//                     </Card>
//                   </Grid>
//                 </Fade>
//               ))}
//           </Grid>
//         </Container>
//       </main> */}
//       <Footer />
//     </>
//   );
// }



// import React, { useState, useEffect } from "react";
// import Button from "@mui/material/Button";
// import Grid from "@mui/material/Grid";
// import Stack from "@mui/material/Stack";
// import Box from "@mui/material/Box";
// import Typography from "@mui/material/Typography";
// import Container from "@mui/material/Container";
// import CssBaseline from "@mui/material/CssBaseline";
// import { useDispatch, useSelector } from "react-redux";
// import { getQualifications } from "../../Slices/qualifications";

// import Card from "@mui/material/Card";
// import CardActions from "@mui/material/CardActions";
// import CardContent from "@mui/material/CardContent";
// import CardMedia from "@mui/material/CardMedia";
// import DropdownShareButton from "../../Components/DropdownShareButton";
// import { Fade } from "@mui/material";
// import { getCertificates } from "../../Slices/certificates";
// import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
// import { Footer } from "../../Components/Footer";

// const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

// export default function Qualifications() {
//   useDocumentTitle("kwalifikacje");
//   const { user } = useSelector((state) => state.auth);
//   const [displaySelected, setDisplaySelected] = useState(false);
//   const [filtered, setFiltered] = useState();
//   const dispatch = useDispatch();
//   const yourTypes = useSelector((state) =>
//     state.certificates.reduce(
//       (unique, item) =>
//         unique.includes(item.attributes?.type)
//           ? unique
//           : [...unique, item.attributes?.type],
//       []
//     )
//   );
//   const qualifications = useSelector((state) => state.qualifications);
//   const filteredQualifications = (type) =>
//     type
//       .map((type) =>
//         qualifications.filter((el) => el.attributes.type === type && el)
//       )
//       .flat();

//   useEffect(() => {
//     if (qualifications) {
//       dispatch(getQualifications());
//       dispatch(getCertificates(user.email));
//     }
//     setFiltered(filteredQualifications(yourTypes));
//   }, [displaySelected]);

//   return (
//     <>
//       <CssBaseline />
//       <main>
//         <Box
//           sx={{
//             bgcolor: "background.paper",
//             pt: 8,
//             pb: 6,
//           }}
//         >
//           <Container maxWidth="sm">
//             <Typography
//               component="h1"
//               variant="h2"
//               align="center"
//               color="text.primary"
//             >
//               Kwalifikacje dla Ciebie
//             </Typography>
//             <Typography
//               variant="h5"
//               align="center"
//               color="text.secondary"
//               paragraph
//             >
//               Na bazie posiadanych przez Ciebie certyfikatów wybraliśmy
//               Kwalifikacje, którymi możesz być zainteresowany.
//             </Typography>
//             <Stack
//               sx={{ pt: 4 }}
//               direction="row"
//               spacing={2}
//               justifyContent="center"
//             >
//               <Button
//                 variant={displaySelected ? "contained" : "outlined"}
//                 onClick={() => setDisplaySelected(true)}
//               >
//                 Wybrane dla Ciebie
//               </Button>
//               <Button
//                 variant={displaySelected ? "outlined" : "contained"}
//                 onClick={() => setDisplaySelected(false)}
//               >
//                 Wszystkie
//               </Button>
//             </Stack>
//           </Container>
//         </Box>
//         <Container sx={{ py: 8 }} maxWidth="md">
//           <Grid container spacing={4}>
//             {displaySelected &&
//               filtered.map(({ id, attributes }) => (
//                 <Fade key={id} in={true} timeout={400}>
//                   <Grid item key={id} xs={12} sm={6} md={6}>
//                     <Card sx={{ height: "100%" }}>
//                       <CardMedia
//                         component="img"
//                         alt="zdjęcie"
//                         height="140"
//                         image={
//                           attributes.src.data
//                             // ? `${UPLOADS_URL}${attributes.src.data[0].attributes.url}`
//                             ? `${attributes.src.data[0].attributes.url}`
//                             : null
//                         }
//                       />
//                       <CardContent>
//                         <Typography variant="h5" component="div">
//                           {attributes.name}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {attributes.desc}
//                         </Typography>
//                       </CardContent>
//                       <CardActions>
//                         <DropdownShareButton shareUrl={attributes.share_url} />
//                         <a
//                           href={attributes.share_url}
//                           style={{
//                             margin: "0px 0px 0px auto",
//                             textDecoration: "none",
//                           }}
//                           target="_blank"
//                         >
//                           <Button size="small">Dowiedz się więcej</Button>
//                         </a>
//                       </CardActions>
//                     </Card>
//                   </Grid>
//                 </Fade>
//               ))}
//             {!displaySelected &&
//               Array.isArray(qualifications) &&
//               qualifications.map(({ id, attributes }) => (
//                 <Fade key={id} in={true} timeout={400}>
//                   <Grid item key={id} xs={12} sm={6} md={6}>
//                     <Card sx={{ height: "100%" }}>
//                       <CardMedia
//                         component="img"
//                         alt="zdjęcie"
//                         height="140"
//                         image={
//                           attributes.src.data
//                             // ? `${UPLOADS_URL}${attributes.src.data[0].attributes.url}`
//                             ? `${attributes.src.data[0].attributes.url}`
//                             : null
//                         }
//                       />
//                       <CardContent>
//                         <Typography variant="h5" component="div">
//                           {attributes.name}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {attributes.desc}
//                         </Typography>
//                       </CardContent>
//                       <CardActions>
//                         <DropdownShareButton shareUrl={attributes.share_url} />
//                         <a
//                           href={attributes.share_url}
//                           style={{
//                             margin: "0px 0px 0px auto",
//                             textDecoration: "none",
//                           }}
//                           target="_blank"
//                         >
//                           <Button size="small">Dowiedz się więcej</Button>
//                         </a>
//                       </CardActions>
//                     </Card>
//                   </Grid>
//                 </Fade>
//               ))}
//           </Grid>
//         </Container>
//       </main>
//       <Footer />
//     </>
//   );
// }
